import classNames from 'classnames'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { SelectButton } from 'primereact/selectbutton'
import React, { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  API_EDUCATION_REPORT,
  API_EDUCATION_REPORT_BY_APPRENTICE_SEMESTER,
  API_EDUCATION_REPORT_UPLOAD,
  API_EDUCATION_REPORTS,
} from '../../../../constants'
import {
  EducationReport,
  EducationReportDb,
  SubCompetenceAssessment,
} from '../../../../model/enums/educationReport.model'
import { RequestMethod, Semester } from '../../../../model/enums/enums'
import { ocrProposalProps } from '../../../../model/ocrProposal.model'
import { useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { handleFileUploadByFile } from '../../../../services/api/handleFileUpload'
import { findApprenticeById } from '../../../../util/arraySearch'
import { getPersonName } from '../../../../util/getPersonName'
import { trimSpaces } from '../../../../util/trimSpaces'
import { getFormErrorMessage } from '../../../util/ErrorText'
import { ApprenticeDropdown } from '../../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import { EmployeeDropdown } from '../../../util/form/dropdown/person-dropdown/EmployeeDropdown'
import {
  defaultEducationReport,
  formatEducationReport,
} from '../../document/education-report/services/educationReportHelper'

export const EducationReportProposalDialog = (
  props: ocrProposalProps
): JSX.Element => {
  const apprentices = useAppSelector((state) => state.apprentices)

  const [newReportConfirmDialogOpen, setNewReportConfirmDialogOpen] =
    useState<boolean>(false)
  const [overwriteReportConfirmDialog, setOverwriteReportConfirmDialog] =
    useState<boolean>(false)
  // make sure we don't need two requests to DB for already subscribed reports
  const [currentEducationReport, setCurrentEducationReport] =
    useState<EducationReport>()

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: props.defaultValues,
    shouldFocusError: false,
  })

  const handleUploadResultCallback = async (_: number | undefined) => {
    props.toastRef.current?.show({
      severity: 'success',
      closable: true,
      summary: t<string>('ocr.upload_successful'),
      detail: t<string>('ocr.edu_upload_successful'),
    })

    setCurrentEducationReport(undefined)
    props.closeCallback()
    return
  }

  const concatComps = (
    comps: any,
    reportId: number
  ): SubCompetenceAssessment[] => {
    const concatenatedSubCompetences: SubCompetenceAssessment[] =
      comps[0].sub_competence_assessment
        .concat(comps[1].sub_competence_assessment)
        .concat(comps[2].sub_competence_assessment)
        .concat(comps[3].sub_competence_assessment)
        .concat(comps[4].sub_competence_assessment)
        .concat(comps[5].sub_competence_assessment)
    const filledCompetences: SubCompetenceAssessment[] = []

    concatenatedSubCompetences.forEach(
      (subCompetence: SubCompetenceAssessment) => {
        subCompetence.id_education_report = reportId
        if (subCompetence.rating && subCompetence.rating > 0) {
          filledCompetences.push(subCompetence)
        }
        // this happens if we add and remove again
        if (subCompetence.rating === null) {
          subCompetence.rating = 0
        }

        if (
          (subCompetence.rating <= 0 || !subCompetence.rating) &&
          subCompetence.reason
        ) {
          console.warn(
            `[Education Report] Losing data for sub-competence with id ${subCompetence.id_sub_competence}.
		            Because no rating is set but a reason.`
          )
        }
      }
    )
    return filledCompetences
  }

  const uploadFile = async (report: EducationReport) => {
    const educationReportID = report.education_report_id

    if (educationReportID) {
      const uploadProps = {
        documentId: educationReportID,
        uploadUrl: API_EDUCATION_REPORT_UPLOAD(educationReportID),
        handleUploadResult: handleUploadResultCallback,
      }
      if (methods.getValues('id_responsible') !== report.id_responsible) {
        report.id_responsible = methods.getValues('id_responsible')
        const concatSubComps = concatComps(report.competence, educationReportID)
        const body: EducationReportDb = {
          education_report_id: report.education_report_id,
          id_apprentice: report.id_apprentice,
          semester_report: report.semester_report,
          course_agreement: trimSpaces(report.course_agreement),
          discussion_date: report.discussion_date,
          education_person_ct: report.education_person_ct,
          feedback_apprenticeship_by_apprentice: trimSpaces(
            report.feedback_apprenticeship_by_apprentice
          ),
          feedback_responsible_by_apprentice: trimSpaces(
            report.feedback_responsible_by_apprentice
          ),
          miscellaneous: trimSpaces(report.miscellaneous),
          signed_file_reference: report.signed_file_reference,
          submission_date: report.submission_date
            ? dayjs(report.submission_date).format('YYYY-MM-DD').toString()
            : undefined,
          closing_date: report.closing_date
            ? dayjs(report.closing_date).format('YYYY-MM-DD').toString()
            : undefined,
          is_active: report.is_active,
          last_change: report.last_change,
          target_achievement_note: report.target_achievement_note,
          sub_competence_assessment: concatSubComps,
          observations: report.observations ? report.observations : [],
          id_responsible: report.id_responsible,
        }
        await handleRequest<EducationReportDb>(
          API_EDUCATION_REPORT(educationReportID),
          RequestMethod.PUT,
          body
        )
      }
      if (props.file) {
        await handleFileUploadByFile(uploadProps)(props.file).catch((err) =>
          console.log(err)
        )
      }
    }
  }

  const onSubmit = async (fileData: any) => {
    const edReportResp = await handleRequest<EducationReportDb[]>(
      API_EDUCATION_REPORT_BY_APPRENTICE_SEMESTER(
        fileData.id_apprentice,
        fileData.semester_report
      ),
      RequestMethod.GET
    )

    const edReport = edReportResp.data
    // no education report present
    if (edReport) {
      if (edReport.length === 0) {
        setNewReportConfirmDialogOpen(true)
        return
      } else {
        const formattedEdReport = formatEducationReport(edReport[0])
        setCurrentEducationReport(formattedEdReport)
        if (formattedEdReport.signed_file_reference) {
          setOverwriteReportConfirmDialog(true)
          return
        }
        uploadFile(formattedEdReport)
      }
    }
  }

  const semester = [
    {
      value: Semester.sem1,
      label: t<string>(`core.education.sem${Semester.sem1}`),
    },
    {
      value: Semester.sem2,
      label: t<string>(`core.education.sem${Semester.sem2}`),
    },
    {
      value: Semester.sem3,
      label: t<string>(`core.education.sem${Semester.sem3}`),
    },
    {
      value: Semester.sem4,
      label: t<string>(`core.education.sem${Semester.sem4}`),
    },
    {
      value: Semester.sem5,
      label: t<string>(`core.education.sem${Semester.sem5}`),
    },
    {
      value: Semester.sem6,
      label: t<string>(`core.education.sem${Semester.sem6}`),
    },
    {
      value: Semester.sem7,
      label: t<string>(`core.education.sem${Semester.sem7}`),
    },
    {
      value: Semester.sem8,
      label: t<string>(`core.education.sem${Semester.sem8}`),
    },
  ]

  const acceptNewEducationReport = async () => {
    // deep copy
    const defaultEdReportCopy: EducationReport = JSON.parse(
      JSON.stringify(defaultEducationReport)
    )
    defaultEdReportCopy.id_apprentice = methods.getValues('id_apprentice')
    defaultEdReportCopy.id_responsible = methods.getValues('id_responsible')
    defaultEdReportCopy.semester_report = methods.getValues('semester_report')

    const res = await handleRequest<EducationReportDb>(
      API_EDUCATION_REPORTS(),
      RequestMethod.POST,
      defaultEdReportCopy
    )
    if (res.data) {
      const edReport = formatEducationReport(res.data)
      uploadFile(edReport)
    }
  }

  const overwriteReport = async () => {
    // should always be true
    if (currentEducationReport) {
      // we need to set the id responsible in case we choose it
      //currentEducationReport.id_responsible = methods.getValues('id_responsible')
      uploadFile(currentEducationReport)
    }
  }

  const getNewReportMessage = (): string => {
    const apprentice = findApprenticeById(
      methods.getValues('id_apprentice'),
      apprentices
    )
    return t<string>('ocr.new_edu_report', {
      semester: methods.getValues('semester_report'),
      name: getPersonName(apprentice),
    })
  }

  const getOverwriteMessage = (): string => {
    const apprentice = findApprenticeById(
      methods.getValues('id_apprentice'),
      apprentices
    )
    return t<string>('ocr.overwrite_edu_report', {
      semester: methods.getValues('semester_report'),
      name: getPersonName(apprentice),
    })
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={'formgrid p-fluid grid pl-2'}>
            <div className={'col-12 formgroup-inline grid mt-1'}>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <ApprenticeDropdown fieldName={'id_apprentice'} />
              </div>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <EmployeeDropdown fieldName={'id_responsible'} />
              </div>
            </div>
            <div className={'formgroup-inline ml-0 col-12'}>
              <label htmlFor={'semester-button'} className={'col-12 pl-0 pt-2'}>
                {t<string>('core.education.semester')}
              </label>
              <Controller
                name="semester_report"
                control={methods.control}
                rules={{ required: t<string>('ocr.semester_missing') }}
                render={({ field, fieldState }) => (
                  <SelectButton
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      'col-12 md:col-12 m-0 mb-2 pt-0 pl-0'
                    )}
                    id={field.name}
                    {...field}
                    options={semester}
                  />
                )}
              />
              {getFormErrorMessage(methods.formState.errors, 'semester_report')}
            </div>
            <div className={'ml-auto mt-2 mr-3 p-fluid grid gap-3'}>
              {/*<Button*/}
              {/*	className={'p-button-outlined col'}*/}
              {/*	label={t<string>('core.cancel')}*/}
              {/*	onClick={() => console.log('todo')}*/}
              {/*/>*/}
              <Button
                label={t<string>('ocr.upload_signed_file')}
                type={'submit'}
                icon="fas fa-file-upload"
                className="p-button col button-height"
              />
            </div>
          </div>
        </form>
      </FormProvider>

      <ConfirmDialog
        visible={newReportConfirmDialogOpen}
        onHide={() => setNewReportConfirmDialogOpen(false)}
        header={t<string>('ocr.new_edu_report_header')}
        message={getNewReportMessage()}
        icon={'pi pi-exclamation-triangle icon-orange'}
        accept={acceptNewEducationReport}
        reject={() => setNewReportConfirmDialogOpen(false)}
        className={'dialog'}
        acceptLabel={t<string>('education_report.upload')}
        rejectLabel={t<string>('core.cancel')}
      />
      <ConfirmDialog
        visible={overwriteReportConfirmDialog}
        onHide={() => setOverwriteReportConfirmDialog(false)}
        header={t<string>('ocr.existing_edu_report_header')}
        message={getOverwriteMessage()}
        icon={'pi pi-exclamation-triangle icon-orange'}
        accept={overwriteReport}
        reject={() => setOverwriteReportConfirmDialog(false)}
        className={'dialog'}
        acceptLabel={t<string>('core.yes')}
        rejectLabel={t<string>('core.no')}
      />
    </>
  )
}